import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import logger from "../utils/logger";
import useFocus from "./useFocus";
import useMarketplace from "./useMarketplace";
import useChat from "./useChat";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// Named constants for magic numbers
const INITIAL_WATCH_DATA = { heartRate: 72, steps: 4532, battery: 85 };
const WATCH_UPDATE_INTERVAL_MS = 5000;
const CLOCK_INTERVAL_MS = 1000;
const HEART_RATE_BASE = 68;
const HEART_RATE_RANGE = 20;
const STEP_INCREMENT_MAX = 10;
const BATTERY_DRAIN_CHANCE = 0.05;
const COUPON_COPY_RESET_MS = 2000;
const DEFAULT_SCHEDULE = { name: "", days: [], start_time: "09:00", duration_minutes: 60 };
const FREE_APP_LIMIT = 3;

export default function useNovaApp(api, user, setUser) {
  // ---- UI State ----
  const [screen, setScreen] = useState("lock");
  const [time, setTime] = useState(new Date());
  const [apps, setApps] = useState([]);
  const [constants, setConstants] = useState({ plans: {}, playlists: [], themes: [], avatars: [], achievements: [], marketplace_items: [] });
  const [watchData, setWatchData] = useState(INITIAL_WATCH_DATA);

  // ---- Modal State ----
  const [showPlans, setShowPlans] = useState(false);
  const [showSpotify, setShowSpotify] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showAppEdit, setShowAppEdit] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);

  // ---- Referral State ----
  const [referralData, setReferralData] = useState(null);
  const [referralInput, setReferralInput] = useState("");
  const [referralMsg, setReferralMsg] = useState(null);
  const [showReferral, setShowReferral] = useState(false);

  // ---- Schedule State ----
  const [schedules, setSchedules] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState(DEFAULT_SCHEDULE);
  const [chartData, setChartData] = useState([]);

  // ---- TTS State ----
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const audioRef = useRef(null);
  const [copiedCoupon, setCopiedCoupon] = useState(null);

  const isPro = user?.current_plan === "pro" || user?.current_plan === "family";

  // ---- Sub-hooks ----
  const focus = useFocus(api, setUser);
  const { focusActive, achievementsData, fetchAchievements, startFocus, endFocus } = focus;

  const marketplace = useMarketplace(api, fetchAchievements);
  const { marketplaceData, marketplaceFilter, setMarketplaceFilter, redeeming, showMarketplace, setShowMarketplace, showRedemptionSuccess, setShowRedemptionSuccess, fetchMarketplace, redeemCoupon } = marketplace;

  const speak = useCallback(async (text) => {
    if (!ttsEnabled) return;
    try {
      setIsSpeaking(true);
      const res = await api.post("/tts", { text, voice: "nova" }, { responseType: "blob" });
      const audioUrl = URL.createObjectURL(res.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); };
        await audioRef.current.play();
      }
    } catch (e) { logger.error("TTS error:", e); setIsSpeaking(false); }
  }, [ttsEnabled, api]);

  const chat = useChat(api, user, speak);
  const { messages, userInput, setUserInput, isSending, messagesEndRef, handleSend, setAIActionHandler } = chat;

  // ---- Fetchers ----
  const fetchConstants = useCallback(async () => {
    try { const r = await api.get("/constants"); setConstants(r.data); } catch (e) { logger.error("Fetch constants:", e); }
  }, [api]);
  const fetchApps = useCallback(async () => {
    try { const r = await api.get("/apps"); setApps(r.data); } catch (e) { logger.error("Fetch apps:", e); }
  }, [api]);
  const fetchReferral = useCallback(async () => {
    try { const r = await api.get("/referrals"); setReferralData(r.data); } catch (e) { logger.error("Fetch referral:", e); }
  }, [api]);
  const fetchSchedules = useCallback(async () => {
    try { const r = await api.get("/schedules"); setSchedules(r.data); } catch (e) { logger.error("Fetch schedules:", e); }
  }, [api]);
  const fetchChartData = useCallback(async () => {
    try { const r = await api.get("/stats/chart"); setChartData(r.data.chart_data || []); } catch (e) { logger.error("Fetch chart:", e); }
  }, [api]);

  // ---- Init ----
  useEffect(() => {
    fetchConstants(); fetchApps(); fetchAchievements(); fetchReferral(); fetchSchedules();
  }, [fetchConstants, fetchApps, fetchAchievements, fetchReferral, fetchSchedules]);

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), CLOCK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user?.watch_connected) return;
    // BATTERY_DRAIN_CHANCE, HEART_RATE_BASE etc. are module-level constants — intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const id = setInterval(() => {
      setWatchData(prev => ({
        heartRate: Math.floor(HEART_RATE_BASE + Math.random() * HEART_RATE_RANGE),
        steps: prev.steps + Math.floor(Math.random() * STEP_INCREMENT_MAX),
        battery: Math.max(0, prev.battery - (Math.random() > (1 - BATTERY_DRAIN_CHANCE) ? 1 : 0))
      }));
    }, WATCH_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user?.watch_connected]);

  // ---- Actions ----
  const updateSettings = useCallback(async (updates) => {
    try { const r = await api.put("/settings", updates); setUser(r.data); }
    catch (e) { logger.error("Update settings:", e); }
  }, [api, setUser]);

  const updateApps = useCallback(async (newApps) => {
    try { await api.put("/apps", newApps); setApps(newApps); }
    catch (e) { logger.error("Update apps:", e); }
  }, [api]);

  const toggleApp = useCallback((appId) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;
    if (!app.allowed && !isPro) {
      if (apps.filter(a => a.allowed).length >= (constants.plans?.free?.app_limit || FREE_APP_LIMIT)) {
        setShowPlans(true); return;
      }
    }
    updateApps(apps.map(a => a.id === appId ? { ...a, allowed: !a.allowed } : a));
  }, [apps, isPro, constants.plans, updateApps, setShowPlans]);

  const handleAIAction = useCallback((action) => {
    const map = {
      start_focus: startFocus,
      stop_focus: endFocus,
      show_spotify: () => { setShowAI(false); setShowSpotify(true); },
      show_stats: () => { setShowAI(false); setShowStats(true); },
      show_plans: () => { setShowAI(false); setShowPlans(true); },
      show_settings: () => { setShowAI(false); setScreen("settings"); },
      show_achievements: () => { setShowAI(false); setShowAchievements(true); },
      show_marketplace: () => { setShowAI(false); fetchMarketplace(); setShowMarketplace(true); },
    };
    map[action]?.();
  }, [startFocus, endFocus, fetchMarketplace, setShowMarketplace, setShowAI, setShowSpotify, setShowStats, setShowPlans, setScreen, setShowAchievements]);

  useEffect(() => { setAIActionHandler(handleAIAction); }, [handleAIAction, setAIActionHandler]);

  const handleCheckout = useCallback(async (planId) => {
    try {
      const res = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) { logger.error("Checkout error:", err); alert("Payment failed to initialize."); }
  }, [api]);

  const applyReferral = useCallback(async () => {
    if (!referralInput.trim()) return;
    setReferralMsg(null);
    try {
      const r = await api.post("/referrals/apply", { referral_code: referralInput });
      setReferralMsg({ type: "success", text: r.data.message });
      setReferralInput(""); fetchAchievements();
    } catch (err) { setReferralMsg({ type: "error", text: fmtErr(err.response?.data?.detail) }); }
  }, [referralInput, api, fetchAchievements]);

  const createSchedule = useCallback(async () => {
    if (!newSchedule.name.trim() || newSchedule.days.length === 0) return;
    try { await api.post("/schedules", newSchedule); setNewSchedule(DEFAULT_SCHEDULE); fetchSchedules(); }
    catch (e) { logger.error("Create schedule:", e); }
  }, [newSchedule, api, fetchSchedules]);

  const deleteSchedule = useCallback(async (id) => {
    try { await api.delete(`/schedules/${id}`); fetchSchedules(); } catch (e) { logger.error("Delete schedule:", e); }
  }, [api, fetchSchedules]);

  const toggleScheduleActive = useCallback(async (id) => {
    try { await api.put(`/schedules/${id}/toggle`); fetchSchedules(); } catch (e) { logger.error("Toggle schedule:", e); }
  }, [api, fetchSchedules]);

  const toggleDay = useCallback((day) => {
    setNewSchedule(prev => ({ ...prev, days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day] }));
  }, []);

  const copyCoupon = useCallback((code) => {
    navigator.clipboard.writeText(code); setCopiedCoupon(code); setTimeout(() => setCopiedCoupon(null), COUPON_COPY_RESET_MS);
  }, []);

  // ---- Computed ----
  const formatTime = useCallback((d) => { const h = d.getHours() % 12 || 12; return `${h}:${d.getMinutes().toString().padStart(2, "0")}`; }, []);
  const formatDate = useCallback((d) => d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }), []);
  const getThemeClass = useCallback(() => ({ default: "theme-default", sunset: "theme-sunset", ocean: "theme-ocean", forest: "theme-forest" }[user?.selected_theme] || "theme-default"), [user?.selected_theme]);
  const getTextColor = useCallback(() => user?.selected_theme === "default" ? "text-dark" : "text-light", [user?.selected_theme]);
  const allowedApps = useMemo(() => apps.filter(a => a.allowed), [apps]);
  const allowedAppsCount = useMemo(() => allowedApps.length, [allowedApps]);
  const filteredMarketplaceItems = useMemo(() => marketplaceData.items.filter(i => marketplaceFilter === "all" || i.category === marketplaceFilter), [marketplaceData.items, marketplaceFilter]);

  return {
    screen, setScreen, focusActive, time, apps, constants, watchData, isPro,
    showPlans, setShowPlans, showSpotify, setShowSpotify, showStats, setShowStats,
    showAI, setShowAI, showAppEdit, setShowAppEdit, showCustomize, setShowCustomize,
    showAchievements, setShowAchievements, showMarketplace, setShowMarketplace,
    showRedemptionSuccess, setShowRedemptionSuccess, marketplaceData, marketplaceFilter,
    setMarketplaceFilter, redeeming, referralData, referralInput, setReferralInput,
    referralMsg, showReferral, setShowReferral, schedules, showSchedule, setShowSchedule,
    newSchedule, setNewSchedule, chartData, messages, userInput, setUserInput,
    isSending, isSpeaking, ttsEnabled, setTtsEnabled, messagesEndRef, audioRef,
    achievementsData, copiedCoupon,
    allowedApps, allowedAppsCount, filteredMarketplaceItems, formatTime, formatDate,
    getThemeClass, getTextColor,
    updateSettings, updateApps, startFocus, endFocus, toggleApp, speak, handleSend,
    handleAIAction, handleCheckout, applyReferral, createSchedule, deleteSchedule,
    toggleScheduleActive, toggleDay, redeemCoupon, copyCoupon,
    fetchAchievements, fetchMarketplace, fetchChartData, fetchSchedules, fetchReferral,
  };
}
